import React, { useState } from 'react';
import { motion } from 'framer-motion';
import LightHeader from './LightHeader';
import ReelPlayer from './ReelPlayer';
import RotatingTag from './RotatingTag';
import HeroStats from './HeroStats';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../translations/translations';

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    visible: (delay = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] },
    }),
};

const maskReveal = {
    hidden: { clipPath: 'inset(0 0 100% 0)' },
    visible: (delay = 0) => ({
        clipPath: 'inset(0 0 0% 0)',
        transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
    }),
};

const GRAIN_URL = `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulance type='fractalNoise' baseFrequency='2.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`;

const HomeHero = () => {
    const { language } = useLanguage();
    const t = useTranslation(language);
    const [isReelOpen, setIsReelOpen] = useState(false);

    const tagWords = [t('heroTagBroadcast'), t('heroTagDev'), t('heroTagTeaching')];

    return (
        <div className="relative min-h-screen bg-black overflow-hidden" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <div
                className="absolute inset-0 opacity-[0.03] mix-blend-overlay pointer-events-none"
                style={{ backgroundImage: GRAIN_URL }}
            />

            <LightHeader ctaLabel={t('getInTouch')} />

            <section className="relative pt-40 pb-24 px-8 md:px-16 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-16 lg:gap-12 items-center">
                <div>
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        custom={0}
                        variants={fadeUp}
                        className="inline-flex items-center gap-2 mb-6"
                    >
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-400" />
                        </span>
                        <span className="text-white/60 text-xs font-mono tracking-widest uppercase">
                            {t('availableForWork')}
                        </span>
                    </motion.div>

                    <motion.p
                        initial="hidden"
                        animate="visible"
                        custom={0.05}
                        variants={fadeUp}
                        className="text-orange-400 text-sm font-mono tracking-widest uppercase mb-6"
                    >
                        <RotatingTag words={tagWords} />
                    </motion.p>

                    <h1 className="text-6xl md:text-7xl lg:text-9xl font-black tracking-tight leading-none mb-8">
                        <motion.div initial="hidden" animate="visible" custom={0.1} variants={maskReveal} className="text-white">
                            Paolo
                        </motion.div>
                        <motion.div initial="hidden" animate="visible" custom={0.2} variants={maskReveal} className="text-orange-400">
                            Allegretti
                        </motion.div>
                    </h1>

                    <motion.p
                        initial="hidden"
                        animate="visible"
                        custom={0.32}
                        variants={fadeUp}
                        className="text-xl md:text-2xl text-white/70 font-light leading-relaxed max-w-xl mb-4"
                    >
                        {t('heroClaim')}
                    </motion.p>

                    <motion.p
                        initial="hidden"
                        animate="visible"
                        custom={0.4}
                        variants={fadeUp}
                        className="text-base md:text-lg text-white/50 font-light leading-relaxed max-w-xl mb-10"
                    >
                        {t('heroBio')}
                    </motion.p>

                    <motion.div
                        initial="hidden"
                        animate="visible"
                        custom={0.48}
                        variants={fadeUp}
                        className="flex flex-wrap items-center gap-8"
                    >
                        <a
                            href="mailto:info@paoloallegretti.com"
                            className="inline-flex items-center text-white font-medium text-lg group"
                        >
                            <span className="border-b border-white/30 group-hover:border-orange-400 group-hover:text-orange-400 transition-colors duration-300 pb-0.5">
                                info@paoloallegretti.com
                            </span>
                            <span className="ml-2 group-hover:translate-x-1 transition-transform duration-300">→</span>
                        </a>

                        <button
                            onClick={() => setIsReelOpen(true)}
                            className="inline-flex items-center gap-3 text-white/70 hover:text-orange-400 font-medium text-lg transition-colors duration-300 group"
                        >
                            <span className="w-9 h-9 rounded-full border border-white/30 group-hover:border-orange-400 flex items-center justify-center transition-colors duration-300">
                                <svg width="10" height="12" viewBox="0 0 10 12" fill="none">
                                    <path d="M1 1L9 6L1 11V1Z" fill="currentColor" />
                                </svg>
                            </span>
                            {t('watchReel')}
                        </button>
                    </motion.div>
                </div>

                <HeroStats />
            </section>

            <ReelPlayer isOpen={isReelOpen} onClose={() => setIsReelOpen(false)} />
        </div>
    );
};

export default HomeHero;
